import { Ticker } from 'pixi.js';
import { clock, update as clockUpdate } from './Clock';
import { DEVELOPMENT, MS_PER_FRAME_60FPS, PROTOCOL_VERSION, RESET_REQUESTED } from './Constants';
import { endFrame as debugEndFrame, setup as debugSetup, startFrame as debugStartFrame } from './Debug';
import { device, setup as deviceSetup } from './Device';
import { reportProtocolMismatch, setup as errorReportSetup } from './ErrorReport';
import { setup as fontsSetup } from './Fonts';
import {
  applySettingsToGame as gamesApplySettingsToGame,
  selectGameFromUrlParams as gamesSelectFromUrl,
  start as gamesStart,
  update as gamesUpdate,
} from './Games';
import {
  render as graphicsRender,
  renderResolution as graphicsRenderResolution,
  setup as graphicsSetup,
  update as graphicsUpdate,
} from './Graphics';
import { hasFocus as inputHasFocus, setup as inputSetup, update as inputUpdate } from './Input';
import { setup as loginSetup } from './Login';
import { update as mobsUpdate } from './Mobs';
import { setup as particlesSetup, update as particlesUpdate } from './particles/Particles';
import { myFlag as playersMyFlag, setMyFlag as playersSetMyFlag, update as playersUpdate } from './Players';
import { updateReel } from './Reel';
import { GAME_STATE_CODES, session } from './Session';
import { auth, loadSettings, setAuth, settings } from './Settings';
import { setup as soundSetup, update as soundUpdate } from './Sound';
import { randomID as toolsRandomID } from './Tools';
import type { AuthConfig } from './types/Auth';
import {
  clearGameNotification as uiClearGameNotification,
  setGameNotification as uiSetGameNotification,
  setup as uiSetup,
  updateMinimapMobAges as uiUpdateMinimapMobAges,
} from './UI';
import { version } from './Version';
import { UNKNOWN_FLAG, isSelectableFlag } from '../services/Flags';
import {
  country as gamesDataCountry,
  isEmpty as gamesDataIsEmpty,
  loaded as gamesDataLoaded,
  onUpdate as gamesDataOnUpdate,
  protocol as gamesDataProtocol,
  regions as gamesDataRegions,
  start as gamesDataStart,
} from '../services/GamesData';
import { clearStorage, loadString } from '../services/LocalStorage';
import '../ui/airmash-ui/airmash-ui';
import { startAssetWarmup } from '../ui/AssetWarmup';
import { GameAPI } from '../ui/GameAPI';
import { initResponsive } from '../ui/Responsive';
import { uiState } from '../ui/UIState';
import { initZoomGuard } from '../ui/ZoomGuard';

/** Logs engine version and a console security warning at startup. */
function logStartupBanner() {
  console.log(`%cΛIRMΛSH Engine ${version} starting up!`, 'font-size: 20px;');
  console.log('');
  console.log('%c*** Important message ***', 'font-size: 16px; color: red;');
  console.log('%cDo not paste any commands given by players in this console window', 'font-size: 14px; color: red;');
  console.log('');
}

async function initialize() {
  // `?reset` — wipe all persisted state for a clean slate before anything
  // reads it (settings, keybinds, saved name, auth, caches). Done first so the
  // rest of startup loads defaults. The param is then stripped from the URL so
  // a refresh doesn't keep wiping and freshly-changed settings can persist.
  if (RESET_REQUESTED) {
    clearStorage();
    try {
      const url = new URL(window.location.href);
      for (const key of [...url.searchParams.keys()]) {
        if (key.toLowerCase() === 'reset') url.searchParams.delete(key);
      }
      window.history.replaceState(null, '', url);
    } catch {
      // history/URL API unavailable — harmless, the wipe already happened
    }
    console.log('[airmash] Cleared localStorage (?reset)');
  }

  // Eruda — mobile devtools overlay, dev mode only. DEVELOPMENT is decided at
  // runtime from the URL, so the chunk ships with the production build and is
  // simply never fetched outside dev mode. Renders a floating button that
  // opens console / network / elements panels on touch devices, where
  // we can't otherwise inspect WebKit without a Mac.
  if (DEVELOPMENT) {
    void import('eruda').then(({ default: eruda }) => {
      eruda.init();
      // Seed bottom-right, lifted clear of the mobile chat drawer's send
      // button; Eruda remembers user-dragged position in localStorage after
      // this, so a manual move sticks across reloads.
      eruda.position({ x: window.innerWidth - 56, y: window.innerHeight - 130 });
    });
  }

  // Dev login tokens — an optional gitignored `.env.local` can carry the
  // vanilla client's localStorage `auth` object (see the placeholder in that
  // file). Vite bakes it into the dev bundle, so LAN devices (the iPhone) get
  // login tokens for token-validating test servers without needing a desktop
  // console to edit localStorage. setAuth persists to localStorage and runs
  // the same completeness validation as a real OAuth login, so from here the
  // flow is identical to vanilla. While the env var exists it re-applies on
  // every load (the file is the source of truth, surviving `?reset`); remove
  // the line from `.env.local` and `?reset` to fully clear.
  if (DEVELOPMENT && import.meta.env.VITE_DEV_AUTH) {
    try {
      setAuth(JSON.parse(import.meta.env.VITE_DEV_AUTH) as AuthConfig);
      if (auth.tokens?.game) {
        console.log('[airmash] Applied login tokens from VITE_DEV_AUTH');
      } else {
        console.warn(
          '[airmash] VITE_DEV_AUTH rejected — auth JSON needs all of tokens.game, tokens.settings, identityprovider, loginname',
        );
      }
    } catch {
      console.warn('[airmash] VITE_DEV_AUTH is not valid JSON — ignored');
    }
  }

  uiState.playerName = loadString('playerName') ?? '';

  logStartupBanner();
  loadSettings();
  deviceSetup(); // Before the settings apply: mouse mode's mobile guard reads device.mobile
  gamesApplySettingsToGame();
  GameAPI.syncState(); // Mirrors the loaded settings for the menu
  initResponsive(); // Wire viewport + input capability state for Lit components
  if (device.mobile) initZoomGuard(); // Snap back any stuck pinch/restored zoom — see ZoomGuard.ts
  errorReportSetup();
  // The renderer and its assets, and the nameplate glyph atlases once the web font is in, together
  await Promise.all([graphicsSetup(), fontsSetup(graphicsRenderResolution())]);
  particlesSetup();
  uiSetup();
  inputSetup();
  loginSetup(() => GameAPI.syncAccountSettings()); // The account's settings replace the loaded ones: the mirror, and the effects of what changed
  soundSetup();
  debugSetup(); // After the systems it counts

  // The loop starts before the games list is fetched, as the original's did:
  // the title reel flies while the lobby waits for the backend.
  const ticker = new Ticker();
  ticker.add((t: Ticker) => scheduleFrame(t.deltaTime));
  ticker.start();

  setInterval(scheduleOccasionalFrameWhileBlurred, 500);

  // Custom server auto-connect (dev only). Two forms:
  //   #connect#ws://host:port — explicit URL in the hash
  //   #connect               — bare, uses VITE_DEV_CONNECT from `.env.local`
  // The bare form exists for LAN devices (the iPhone) where typing a ws://
  // URL is painful — the bookmark is just `?dev#connect`. It must still be
  // opted into per-load via the hash so a filled-in env var never hijacks
  // normal dev sessions against real servers.
  const customServerHashPrefix = '#connect#';
  if (DEVELOPMENT) {
    let customServerUrl: string | undefined;
    if (window.location.hash.startsWith(customServerHashPrefix)) {
      customServerUrl = window.location.hash.substring(customServerHashPrefix.length);
    } else if (window.location.hash === '#connect') {
      customServerUrl = import.meta.env.VITE_DEV_CONNECT;
      if (!customServerUrl) {
        console.warn('[airmash] Bare #connect needs VITE_DEV_CONNECT in .env.local — ignored');
      }
    }
    if (customServerUrl) {
      gamesStart({ kind: 'custom', url: customServerUrl }, settings.name || toolsRandomID(6), true);
    }
  }

  // The first list to arrive settles the flag, the maintenance notice, the
  // protocol check and an invite. On the first answer rather than once the
  // start returns: a first fetch that fails, as on a phone whose network is
  // still waking, had the lobby announce maintenance over a list the next
  // refresh then filled, and dropped an invite link with it. Legacy ran these
  // in its success callback, and never retried a failed fetch.
  let firstListHandled = false;
  let listUnreachableNotice: number | null = null;
  gamesDataOnUpdate((regions, closestRegionId) => {
    uiState.regions = regions;
    uiState.closestRegionId = closestRegionId;
    if (!firstListHandled && gamesDataLoaded) {
      firstListHandled = true;
      if (listUnreachableNotice !== null) uiClearGameNotification(listUnreachableNotice);
      onFirstGamesList();
    }
  });
  await gamesDataStart(true);

  // The start's quick retries done without an answer: say so, since the lobby otherwise sits with PLAY greyed and
  // nothing to explain it, as the original's did. The refresh keeps asking, and the first list clears the notice.
  if (!gamesDataLoaded) {
    listUnreachableNotice = uiSetGameNotification({
      type: 'alert',
      title: '',
      message: 'The server list could not be fetched\nTrying again…',
      showReconnect: false,
      timeoutMs: 0,
    });
  }

  // Boot-critical fetches (Pixi bundles, games data) are done — start
  // trickling the lazily-referenced UI assets into the HTTP cache.
  startAssetWarmup();
}

/** What the backend's first answer decides, run once it has been parsed. */
function onFirstGamesList() {
  // The backend's country for the flag, failing a saved one; a country the client has no flag for leaves the placeholder
  if (playersMyFlag === UNKNOWN_FLAG) {
    const country = gamesDataCountry.toLowerCase();
    if (isSelectableFlag(country)) playersSetMyFlag(country);
  }

  // Handle server maintenance (no games available)
  if (gamesDataIsEmpty()) {
    uiSetGameNotification({
      type: 'alert',
      title: '',
      showReconnect: false,
      timeoutMs: 30000,
      message: 'We are currently performing server maintenance\nPlease try again in a few minutes',
    });
  }

  // Check for protocol mismatch and force reload if needed
  if (gamesDataProtocol !== 0 && PROTOCOL_VERSION !== gamesDataProtocol) {
    if (window.location.hash !== '#reload') {
      // The report is telemetry; the reload must happen either way
      reportProtocolMismatch(false).then(() => {
        uiSetGameNotification({
          type: 'alert',
          title: '',
          showReconnect: false,
          timeoutMs: 30000,
          message: 'Protocol update\nYour client is being updated to the new version',
        });
        setTimeout(() => {
          window.location.href = '/?' + toolsRandomID(10) + '#reload';
        }, 5000);
      });
    } else {
      void reportProtocolMismatch(true);
    }
  }

  // Parse invite URL if games data is available
  if (!gamesDataIsEmpty()) {
    gamesSelectFromUrl(gamesDataRegions);
  }
}

/** Main game loop — updates all systems and optionally renders. */
function scheduleFrame(fractionalFramesSinceLastFrame: number, skipGraphicsRendering = false) {
  clockUpdate(fractionalFramesSinceLastFrame);
  debugStartFrame();

  if (session.state === GAME_STATE_CODES.PLAYING) {
    inputUpdate();
    playersUpdate();
    mobsUpdate();
    particlesUpdate();
    gamesUpdate(false);
    uiUpdateMinimapMobAges();
    soundUpdate();
  } else if (session.state === GAME_STATE_CODES.LOGIN) {
    updateReel();
  } else {
    soundUpdate();
  }

  graphicsUpdate();

  if (!skipGraphicsRendering) {
    graphicsRender();
  }

  debugEndFrame();
}

/** Keeps the game minimally responsive while the window is blurred. */
function scheduleOccasionalFrameWhileBlurred() {
  const msSinceLastFrame = performance.now() - clock.time;
  if (msSinceLastFrame > 450 && !inputHasFocus()) {
    scheduleFrame(msSinceLastFrame / MS_PER_FRAME_60FPS, true);
  }
}

/** The Lit UI renders without the game loop, so a failed boot can still say so instead of leaving a blank page. */
function runInitialize() {
  initialize().catch((error: unknown) => {
    console.error('Failed to initialize game:', error);
    // A missing renderer puts up its own full-screen message (see Graphics.setupPixiRenderer).
    if (uiState.bigMessage !== null) return;
    uiSetGameNotification({
      type: 'alert',
      title: 'STARTUP FAILED',
      message: 'The game could not start\nRefresh the page to try again',
      showReconnect: false,
      timeoutMs: 60000,
    });
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', runInitialize);
} else {
  runInitialize();
}
